// @flow

import React, { PureComponent } from 'react';

import Button from 'components/fragments/button/Button';
import SimpleCarousel from 'components/fragments/SimpleCarousel/SimpleCarousel';

import { MOBILE_SIZE } from 'constants/app';

import type { DisplayModalFunctionType } from 'types/Modal';
import type { Bloc, BlocContent } from 'types/NewsletterBloc';
import type { AlbumType } from 'types/Albums';
import type { Status } from 'types/Status';

export type StateProps = {
  status: Status,
  album: AlbumType
};

export type DispatchProps = {
  displayModal: DisplayModalFunctionType
};

type Props = {
  content: any
} & StateProps &
  DispatchProps;

type State = {
  page: number,
  displayCarousel: boolean,
  focusImg: number,
  mobile: boolean
};

/*const videoTest = {
  tagName: 'video',
  featured_image: {
    sizes: null,
    src: 'https://dow4.sportagraph.com/fa03-8a26-1dc0-4d63a4fb12df_340-550-rgb-width-medium?widgetid=b883-397c-85c9-400bbfb91731'
  },
  featured_video: {
    title: 'vidéo-test',
    duration: '0:34',
    src: 'https://dow4.sportagraph.com/fa03-8a26-1dc0-4d63a4fb12df_1280-720-rgb-height-high--vid?widgetid=b883-397c-85c9-400bbfb91731'
  }
};*/

export default class AlbumGallery extends PureComponent<Props, State> {
  state = {
    page: 0,
    displayCarousel: false,
    focusImg: 0,
    mobile: false
  };

  componentDidMount() {
    this.updateDimensions();
    window.addEventListener('resize', this.updateDimensions.bind(this));
  }

  componentDidUpdate() {
    this.updateDimensions();
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateDimensions.bind(this));
  }

  updateDimensions() {
    const { mobile } = this.state;
    if (!mobile && window.innerWidth < MOBILE_SIZE) {
      this.setState({ mobile: true });
    }
    if (mobile && window.innerWidth >= MOBILE_SIZE) {
      this.setState({ mobile: false });
    }
  }

  handleClickImage = (focusImg: number) => {
    this.setState({
      focusImg,
      displayCarousel: true
    });
  };

  closeCarousel = () => {
    this.setState({ displayCarousel: false });
  };

  renderMosaic = (item: BlocContent | Bloc, i: number) => {
    const {
      featured_image: { src, sizes } = {},
      featured_video: { title: videoTitle, duration: videoDuration } = {},
      featured_video = {},
      tagName,
      figcaption,
      iframe
    } = item;

    if ((tagName === 'video' && Object.keys(featured_video).length > 0) || (tagName !== 'img' && iframe === true)) {
      return (
        <div key={videoTitle || figcaption || 'document_' + i}>
          <div
            role="button"
            tabIndex={0}
            onClick={() => this.handleClickImage(i)}
            title={figcaption}
            style={{ width: '100%', height: 'auto', marginRight: 0 }}
            className="box card card--video "
          >
            <div className="card__thumbnail img-blue-dark" style={{ backgroundImage: `url(${src})` }} />
            <div className="card__footer">
              <span className="link-icon link-icon--medium is-dark">
                <i className="icon icon-play" />
              </span>
              <h5 className="card__title ft-truncat">{videoTitle || figcaption}</h5>
              <p className="card__date">{videoDuration}</p>
            </div>
            <div className="card__type" />
          </div>
        </div>
      );
    }
    const thumbnailSource = sizes && sizes.thumbnail && sizes.thumbnail.src;
    return (
      <div
        key={i}
        role="button"
        tabIndex={0}
        onClick={() => this.handleClickImage(i)}
        title={figcaption}
        style={{ backgroundImage: `url(${thumbnailSource || src})` }}
      />
    );
  };

  renderCol = (isLeft: boolean) => {
    const {
      content: { children }
    } = this.props;

    const { page, mobile } = this.state;
    const paginationIndex = page * 18;
    const leftCol_desktop = [0, 1, 4, 9, 10, 13, 14, 15, 16].map(n => n + paginationIndex);
    const rightCol_desktop = [2, 3, 5, 6, 7, 8, 11, 12, 17].map(n => n + paginationIndex);
    let leftCol = leftCol_desktop;
    let rightCol = rightCol_desktop;
    if (mobile) {
      leftCol = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map(n => n + paginationIndex);
      rightCol = [10, 11, 12, 13, 14, 15, 16].map(n => n + paginationIndex);
    }
    if (children) {
      return children.map((item, i) => {
        if (isLeft && leftCol.includes(i)) {
          return this.renderMosaic(item, i);
        } else if (!isLeft && rightCol.includes(i)) {
          return this.renderMosaic(item, i);
        }
        return null;
      });
    }
    return null;
  };

  backToPrevPage = () => {
    if (this.state.page > 0) {
      this.setState(({ page: prevPage }) => ({ page: prevPage - 1 }));
    }
  };

  goToNextPage = () => {
    const {
      content: { children }
    } = this.props;
    const indicators = new Array(Math.ceil(children.length / 18)).fill({});
    if (indicators.length > this.state.page+1) {
      this.setState(({ page: prevPage }) => ({ page: prevPage + 1 }));
    }
  };

  renderPagesDots = (): React$Element<'li'>[] | null => {
    const {
      content: { children }
    } = this.props;
    const { page } = this.state;
    if (children) {
      const indicators = new Array(Math.ceil(children.length / 18)).fill({});

      return indicators.map((it, i) => {
        const isActive = (page === i && 'is-active') || '';
        const pageNumber = i + 1;
        return (
          <li key={i}>
            {/*eslint-disable-next-line */}
            <a
              role="button"
              tabIndex={0}
              onClick={() => this.setState({ page: i })}
              className={`${isActive} ${pageNumber}`}
              title={`Page ${pageNumber}`}
            >
              {pageNumber}
            </a>
          </li>
        );
      });
    }
    return null;
  };

  renderPagination = () => {
    const { page } = this.state;
    const {
      content: { children }
    } = this.props;
    const disabled = (page === 0 && 'is-disabled') || '';

    const indicators = new Array(Math.ceil(children.length / 18)).fill({});

    const disabled_next = (page === (indicators.length-1) && 'is-disabled') || '';
    return (
      <div className="row mt-2">
        <div className="col col-md-12">
          <ul className="pagination ">
            <li className={`pagination__prev ${disabled}`}>
              {/*eslint-disable-next-line */}
              <a role="button" tabIndex={0} onClick={this.backToPrevPage} title="Page précédente">
                <i className="icon icon-chevron-left" />
              </a>
            </li>
            {this.renderPagesDots()}
            <li className={`pagination__next ${disabled_next}`}>
              {/*eslint-disable-next-line */}
              <a role="button" tabIndex={0} onClick={this.goToNextPage} title="Page suivante">
                <i className="icon icon-chevron-right" />
              </a>
            </li>
          </ul>
        </div>
      </div>
    );
  };

  handleClickModal = () => {
    const {
      displayModal,
      album: { title }
    } = this.props;
    displayModal('MEDIA_SUBMISSION', { title }, true);
  };

  render() {
    const {
      content: { children }
    } = this.props;
    const { displayCarousel, focusImg } = this.state;

    return (
      <>
        <div className="box card card--interact box--smoke mt-4 mb-4 mb-md-3 mb-md-5">
          <div className="card__body">
            <p className="ft-h4">Partagez vos meilleurs clichés, et contribuez à la vie du club !</p>
          </div>
          <Button className="btn btn--primary btn--full" onClick={this.handleClickModal}>
            <i className="icon  is-inline" />
            <span>Soumettre un contenu</span>
          </Button>
        </div>
        <div className="mosaic-images">
          <div className="mosaic-images__left">{this.renderCol(true)}</div>
          <div className="mosaic-images__right">{this.renderCol(false)}</div>
        </div>
        {children && children.length > 18 && this.renderPagination()}
        {displayCarousel && children && (
          <SimpleCarousel
            onCloseRequest={this.closeCarousel}
            data={children} //@TODO : remove videoTest when there'll be real test videos
            initialIndex={focusImg}
          />
        )}
      </>
    );
  }
}
